import { ShoppingCartIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useState } from 'react';
import { Billing, Organization } from '../../../shared/types';
import Loading from '../../Loading';
import { apiGet } from '../../../utils/api';

interface OrganizationBillingTabProps {
  organization: Organization;
}

const OrganizationBillingTab = ({ organization }: OrganizationBillingTabProps) => {
  const [ billing, setBilling ] = useState<Billing | null>(null);
  const [ isLoading, setIsLoading ] = useState(true);
  const [ error, setError ] = useState<string | null>(null);

  const fetchBilling = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const response = await apiGet(`/api/${organization.id}/billing`);
    const data = await response.json();
    if (!data.error) {
      setBilling(data);
    } else {
      setError(data.error);
    }
    setIsLoading(false);
  }, [ organization.id ]);

  const handleManageSubscription = async () => {
    try {
      const response = await apiGet(`/api/${organization.id}/billing/open-subscription-portal`);

      if (response.ok) {
        const { url } = await response.json();
        window.location.href = url;
      } else {
        const errorData = await response.json();
        console.error('Failed to open subscription portal:', errorData.error);
        setError('Failed to open subscription management portal');
      }
    } catch (error) {
      console.error('Error opening subscription portal:', error);
      setError('Failed to open subscription management portal');
    }
  };

  useEffect(() => {
    fetchBilling();
  }, [ fetchBilling ]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Billing & Subscriptions</h2>
          <p className="text-gray-600 mt-2">Manage your organization's billing and subscription settings</p>
        </div>
        <a
          href={`/${organization.id}/billing/checkout`}
          className="bg-brand-600 hover:bg-brand-700 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center gap-2"
        >
          <ShoppingCartIcon className="w-5 h-5" />
          Find Subscription Plans
        </a>
      </div>

      {isLoading && <Loading message="Loading Billing Data..." />}

      {billing && !error && (
        <div className="bg-gradient-to-r from-brand-50 to-indigo-50 rounded-xl p-6 border border-brand-200">
          <div className="flex items-start gap-4">
            <div className="bg-brand-100 rounded-lg p-3">
              <svg className="w-6 h-6 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-brand-900 mb-2">Current Subscription</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-brand-600 font-medium">Status</p>
                  <p className="text-brand-900 font-semibold capitalize">{billing.status}</p>
                </div>
                <div>
                  <p className="text-sm text-brand-600 font-medium">Customer Email</p>
                  <p className="text-brand-900 font-mono text-sm">{billing.customer_email || 'Email not found'}</p>
                </div>
              </div>

              <div className="bg-white/50 rounded-lg p-3 mb-3">
                <p className="text-sm text-brand-600 font-medium mb-1">Current Period</p>
                <p className="text-brand-900">
                  {new Date(billing.current_billing_period_start * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  <span> - {new Date(billing.current_billing_period_end * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </p>
              </div>

              <div className="bg-white/50 rounded-lg p-3 mb-3">
                <p className="text-sm text-brand-600 font-medium mb-1">Subscription Created</p>
                <p className="text-brand-900">
                  {new Date(billing.subscription_creation_date * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              </div>

              <div className="bg-white/50 rounded-lg p-3 mb-3">
                <p className="text-sm text-brand-600 font-medium mb-1">Billing Interval</p>
                <p className="text-brand-900 capitalize">{billing.subscription_interval}</p>
              </div>

              {billing.subscription_product_id && (
                <div className="bg-white/50 rounded-lg p-3 mb-3">
                  <p className="text-sm text-brand-600 font-medium mb-1">Product ID</p>
                  <p className="text-brand-900 font-mono text-sm">{billing.subscription_product_id}</p>
                </div>
              )}

              {billing.cancel_at && billing.cancel_at > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-700 font-medium mb-1">⚠️ Subscription Cancellation</p>
                  <p className="text-yellow-800">
                    Will cancel on: {new Date(billing.cancel_at * 1000).toLocaleDateString()}
                  </p>
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-brand-200">
                <button
                  onClick={handleManageSubscription}
                  className="bg-brand-600 hover:bg-brand-700 text-white font-medium py-2 px-4 rounded-md transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Manage Subscription
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <div className="bg-red-100 rounded-lg p-2 flex-shrink-0">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-red-900 mb-2">Billing Information Unavailable</h3>
              <p className="text-red-700 mb-3">
                {error === "Billing data not found"
                  ? "We couldn't retrieve your billing information at this time. This might be because billing hasn't been set up yet or there was a temporary issue."
                  : error
                }
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={fetchBilling}
                  className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium py-2 px-4 rounded-md transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationBillingTab;
